import { Dialog, DialogActions, DialogContent, DialogTitle, Typography, Button, Box, IconButton, useTheme, CircularProgress, TextField } from '@mui/material'
import { Warning } from '@mui/icons-material'
import { Icons } from '../../utils/utilities'
import React, { useState } from 'react'
import PropTypes from 'prop-types'

/**
 * Confirmation dialog with secondary text confirmation
 *
 * @param {Object} props
 * @param {import('@mui/material').Color} [props.color] - The secondary confirmation field color.
 * @param {import('@mui/material').Color} [props.themeColor] - The band and button color.
 * @param {Boolean} [props.enableSecondaryConfirmation=false] - Enable secondary confirmation.
 * @param {String} [props.secondaryConfirmationText="Confirm"] - Text to be typed to satisfy secondary confirmation.
 * @param {String} [props.waiting] - True when the confirm action is running.
 * @param {String} props.actionName - Name of the action to be performed.
 * @param {Function} props.confirmAction - Action to be performed on confirm.
 * @param {String} props.message - Message to be displayed on the dialog.
 * @param {Boolean} props.status - True when the dialog is open.
 * @param {Function} props.cancelAction - Action to be performed on cancel.
 * @param {Boolean} [props.isReversible] - True if this action is reversible.
 */

const ConfirmDialog = ( { color = "secondary", themeColor = "error", enableSecondaryConfirmation = false, secondaryConfirmationText = 'confirm', waiting, actionName = "confirm", confirmAction, message, status, cancelAction, isReversible } ) => {

    const [secondaryConfirmationValue, setSecondaryConfirmationValue] = useState( '' )

    const { palette, border } = useTheme()

    const triggerCancelAction = () => {
        setSecondaryConfirmationValue( "" )
        cancelAction()
    }

    return (
        <Dialog id="delete-confirm-dialog" open={status} slotProps={{
            paper: { sx: { width: "400px", minWidth: "200px", maxWidth: "500px" } }
        }}>
            <DialogTitle sx={{ fontSize: "16px", justifyContent: "space-between", display: "flex", padding: "10px 20px", alignItems: "center", gap: "20px" }}>
                Are you sure?
                <IconButton onClick={triggerCancelAction}>
                    {Icons.default.CloseIcon}
                </IconButton>
            </DialogTitle>
            <DialogContent className='custom-scrollbar' sx={{ padding: 0 }}>
                <Box>
                    {!isReversible && <Typography position="sticky" top="0" padding="10px 20px" bgcolor={palette[themeColor] ? palette[themeColor]?.dark + "22" : themeColor} gap="10px" display="flex" alignItems="center" fontSize="12px" fontWeight="500">
                        <Warning fontSize='small' />
                        This action cannot be undone.
                    </Typography>}
                    <Box padding="20px" fontSize="14px">{message}</Box>
                    {enableSecondaryConfirmation && <Box marginBottom="20px" padding="0 20px">
                        <TextField color={color} fullWidth placeholder={secondaryConfirmationText} value={secondaryConfirmationValue} onChange={e => setSecondaryConfirmationValue( e.target.value )} size="small" slotProps={{
                            input: { sx: { fontSize: "14px", fontStyle: "italic" } }
                        }} />
                        <Typography marginTop="10px" fontSize="12px">Type <em> <strong>{secondaryConfirmationText}</strong> </em> to confirm.</Typography>
                    </Box>}
                </Box>
            </DialogContent>
            <DialogActions sx={{ borderTop: border[1] }}>
                <Button id="delete-confirm-btn" startIcon={waiting && <CircularProgress size={14} />} disabled={waiting || ( enableSecondaryConfirmation && secondaryConfirmationText !== secondaryConfirmationValue )} disableElevation color={palette[themeColor] ? themeColor : "error"} variant='contained' onClick={confirmAction} sx={{ textTransform: "capitalize" }}>{actionName}</Button>
            </DialogActions>
        </Dialog>
    )
}

ConfirmDialog.propTypes = {
    enableSecondaryConfirmation: PropTypes.bool,
    secondaryConfirmationText: PropTypes.string,
    waiting: PropTypes.bool,
    status: PropTypes.bool,
    isReversible: PropTypes.bool,
    message: PropTypes.node,
    cancelAction: PropTypes.func.isRequired,
    confirmAction: PropTypes.func.isRequired,
    actionName: PropTypes.string
}

export default ConfirmDialog
